import createClient from "openapi-fetch";
import type { paths as PlatformPaths, components as PlatformComponents } from "../types/platform/v1";
import type { paths as AdminPaths } from "../types/admin/v1";
import { getEnvVar } from "./utils";

const platformClient = createClient<PlatformPaths>({
  baseUrl: getEnvVar('VITE_API_BASE_URL') || "http://localhost:8086/",
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include',
});

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// --- Multi-backend target management ---
let _proxyTarget: string | undefined;
type TargetChangeListener = (target: string | undefined) => void;
const _listeners = new Set<TargetChangeListener>();

export function setProxyTarget(target: string | undefined) {
  _proxyTarget = target;
  _listeners.forEach(fn => fn(target));
}

export function getProxyTarget(): string | undefined {
  return _proxyTarget;
}

export function onProxyTargetChange(fn: TargetChangeListener): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

function getStatusText(statusCode: number): string {
  const statusTexts: Record<number, string> = {
    200: 'OK',
    201: 'Created',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Conflict',
    422: 'Unprocessable Entity',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
  };
  
  return statusTexts[statusCode] || (statusCode < 400 ? 'OK' : 'Error');
}

/**
 * Custom fetch function that proxies requests through the platform's director proxy
 */
const proxyFetch: typeof fetch = async (input, init) => {
  // Handle both string URLs and Request objects
  let url: string;
  let method: HttpMethod;
  let requestBody: BodyInit | null = null;
  let requestHeaders: HeadersInit | undefined;
  
  if (typeof input === 'string') {
    url = input;
    method = (init?.method || 'GET') as HttpMethod;
    requestBody = init?.body || null;
    requestHeaders = init?.headers;
  } else {
    // openapi-fetch 0.14 calls fetch(request) with no init. Firefox leaves
    // Request.body null for string bodies — must use text()/json().
    url = input.url;
    method = (init?.method || input.method) as HttpMethod;
    requestHeaders = init?.headers ?? input.headers;
    if (init?.body != null) {
      requestBody = init.body;
    } else if (!input.bodyUsed) {
      requestBody = await input.clone().text();
    }
  }
  
  // Parse the URL to extract the path (remove the base URL)
  const urlObj = new URL(url);
  const path = urlObj.pathname + urlObj.search;
  
  // Extract query parameters
  const queryParams: Record<string, string> = {};
  urlObj.searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  
  // Parse body if present
  let body: any;
  if (requestBody) {
    if (typeof requestBody === 'string') {
      try {
        body = JSON.parse(requestBody);
      } catch {
        body = requestBody;
      }
    } else if (requestBody instanceof ReadableStream) {
      // Handle ReadableStream body (from Request objects)
      const reader = requestBody.getReader();
      const chunks: Uint8Array[] = [];
      let done = false;
      
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          chunks.push(value);
        }
      }
      
      // Combine chunks and decode as text
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }
      
      const bodyText = new TextDecoder().decode(combined);
      try {
        body = JSON.parse(bodyText);
      } catch {
        body = bodyText;
      }
    } else {
      body = requestBody;
    }
  }
  
  // Convert headers to plain object
  let headersObj: Record<string, string> | undefined;
  if (requestHeaders) {
    headersObj = {};
    if (requestHeaders instanceof Headers) {
      requestHeaders.forEach((value, key) => {
        headersObj![key] = value;
      });
    } else if (Array.isArray(requestHeaders)) {
      requestHeaders.forEach(([key, value]) => {
        headersObj![key] = value;
      });
    } else {
      headersObj = requestHeaders as Record<string, string>;
    }
  }
  
  // Create the proxy request
  const proxyRequest: PlatformComponents["schemas"]["ProxyRequest"] = {
    url: path,
    method,
    data: body,
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    headers: headersObj,
    target: _proxyTarget ?? undefined,
  };
  
  // Make the proxied request
  const { data, error, response } = await platformClient.POST("/v1/director/proxy", {
    body: proxyRequest,
  });
  
  if (error) {
    // The proxy returns the actual status code from the proxied request
    // So we use the response status if available, otherwise default to 500
    const status = response?.status || 500;
    const statusText = getStatusText(status);
    
    return new Response(JSON.stringify(error), {
      status,
      statusText,
      headers: response?.headers || { 'Content-Type': 'application/json' },
    });
  }
  
  // The proxy returns the body directly with the proper status code
  // The response object contains the actual HTTP response details
  return new Response(JSON.stringify(data), {
    status: response.status,
    statusText: response.statusText || getStatusText(response.status),
    headers: response.headers,
  });
};

/**
 * Admin client created with openapi-fetch that uses the proxy fetch
 * This can be used directly with openapi-react-query
 */
export const adminProxyClient = createClient<AdminPaths>({
  // The baseUrl doesn't matter since we're intercepting all requests
  baseUrl: "https://admin-api",
  fetch: proxyFetch,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export as default for backward compatibility
export default adminProxyClient;


